import Moment from 'moment';
import {extendMoment} from 'moment-range'
import {mapActions, mapState} from "pinia";
import {useMainStore} from "../state/store";
import {useAnalyticsStore} from "../state/modules/analytics";
import {useCompaniesStore} from "../state/modules/companies";

const moment = extendMoment(Moment);
export default {
    data() {
        return {
            handlingData: false,
            fetching: true,
            dateLimitation: 3, // set to 3 years, to optimize the performance of the datepicker
            chartDataLimit: 30,
            series: [],
            seriesFormat: null,

            selectedTab: null,
            showAllData: false,
            defaultDataThreshold: 10,

            lastRequestTime: null,

            currentPage: 1,

            graphData: null,
            graphRef: null,

            lockFetch: false,
        }
    },
    computed: {
        ...mapState(useMainStore, ['selectedLocale']),
        ...mapState(useAnalyticsStore, ['getFrom', 'getTo', 'getCompanyCreatedAt', 'getDatesToFill']),
        now() {
            return moment();
        },
        seriesDataLength() {
            if (!this.series.length) return 0;
            if (this.series.length > 1) {
                return Math.max(...this.series.map(series => series.data.length));
            }
            return this.series[0].data?.length;
        },
        from: {
            get() {
                return this.getFrom;
            },
            set(value) {
                this.setFrom(value);
            }
        },
        to: {
            get() {
                return this.getTo;
            },
            set(value) {
                this.setTo(value);
            }
        },
        pastLimit() {
            return moment(this.getCompanyCreatedAt);
        },
        totalPages() {
            return Math.ceil(this.graphData.count / this.defaultDataThreshold);
        }
    },
    methods: {
        ...mapActions(useCompaniesStore, ['creationDate']),
        ...mapActions(useAnalyticsStore, ['setFrom', 'setTo', 'setCompanyCreatedAt']),
        parseKey(key) {
            if (this.seriesFormat === "days") {
                return moment(key).locale(this.selectedLocale).format('DD-MM-YY');
            } else if (this.seriesFormat === "weeks") {
                return moment(key, "w YYYY").locale(this.selectedLocale).format('wo') + " " + this.$t('analytics.week') + " " + moment(key, "w YYYY").locale(this.selectedLocale).format("'YY");
            } else {
                return moment(key).locale(this.selectedLocale).format('MMM YY');
            }
        },
        formatCategoryFull(category) {
            if (this.seriesFormat === "days") {
                return moment(category, "MMM D 'YY", this.selectedLocale).locale(this.selectedLocale).format('DD MMMM YYYY');
            } else if (this.seriesFormat === "weeks") {
                let [week, string, year] = category.split(' ');
                // remove extra characters from week that are not a digit
                week = week.replace(/\D/g, '');
                // get the first date of the week and the last date of the week
                let firstDay = moment(week + ' ' + year, "W 'YY", this.selectedLocale).startOf('week').locale(this.selectedLocale);
                let lastDay = moment(week + ' ' + year, "W 'YY", this.selectedLocale).endOf('week').locale(this.selectedLocale);
                return `${firstDay.format('DD MMMM')} - ${lastDay.format('DD MMMM YYYY')}`;
            } else {
                return moment(category, 'MMM YY', this.selectedLocale).locale(this.selectedLocale).format('MMMM YYYY');
            }
        },
        getTabColor(tab) {
            return this.chartOptions.colors[this.tabs.indexOf(tab)];
        },
        handleUpdateChart(value) {
            if (this.selectedTab === value) {
                this.selectedTab = null;
                return;
            }
            this.selectedTab = value;
        },
        sendRequest() {
            if (this.lastRequestTime) {
                let diff = moment().diff(this.lastRequestTime, 'minute');
                if (diff < 5) {
                    return false;
                }
            }
            this.lastRequestTime = moment();
            return true;
        },
        getPageData() {
            let data = JSON.parse(JSON.stringify(this.graphData.data));
            // check if data is an array of objects, or just an object
            if (Array.isArray(data)) {
                data = data.map(item => {
                    item.data = item.data.slice((this.currentPage - 1) * this.defaultDataThreshold, this.currentPage * this.defaultDataThreshold);
                    return item;
                })
                return data;
            } else {
                data.data = data.data.slice((this.currentPage - 1) * this.defaultDataThreshold, this.currentPage * this.defaultDataThreshold);
                return [data];
            }
        },
        getTranslation(value) {
            if (this.translationKey) {
                return this.$t(this.translationKey + '.' + value)
            } else {
                return false;
            }
        },
        handleGraphFilter() {
            this.fetching = true;
            if (typeof this.fetchData === 'function') {
                this.fetchData();
            }
        },
        setDownloadButtonId(graphRef, staticId) {
            this.$nextTick(() => {
                graphRef?.$el.querySelector('.apexcharts-menu-icon button').setAttribute('id', staticId);
            });
        },
    },
    watch: {
        filterFields() {
            this.lastRequestTime = null;
            this.fetching = true;
            if (this.selectedJobId) {
                this.selectedJobId = null;
            }
            if (this.loadData) {
                this.loadData = false;
            }
            if (typeof this.fetchData === 'function') {
                this.fetchData()
            }

            if (this.currentPage !== 1) {
                this.currentPage = 1;
            }
        },
        'filterFields.dateFromApplications'() {
            if (this.filterFields.dateFromApplications) {
                this.graphFrom = this.filterFields.dateFromApplications;
            } else {
                this.graphFrom = this.getCompanyCreatedAt;
            }
        },
        'filterFields.dateToApplications'() {
            if (this.filterFields.dateToApplications) {
                this.graphTo = this.filterFields.dateToApplications;
            } else {
                this.graphTo = this.now;
            }
        },
        'filterFields.dateFromJobs'() {
            if (this.filterFields.dateFromJobs) {
                this.graphFrom = this.filterFields.dateFromJobs;
            } else {
                this.graphFrom = this.getCompanyCreatedAt;
            }
        },
        'filterFields.dateToJobs'() {
            if (this.filterFields.dateToJobs) {
                this.graphTo = this.filterFields.dateToJobs;
            } else {
                this.graphTo = this.now;
            }
        },
        currentPage() {
            this.series = this.getPageData();
            this.graphRef.updateOptions({
                ...this.chartOptions,
                dataLabels: {
                    enabled: true,
                },
            });
            if (this.selectedTab && this.getTranslation(this.selectedTab)) {
                this.graphRef.hideSeries(this.getTranslation(this.selectedTab));
            }
        }
    },
    created() {
        this.graphFrom = this.from;
        this.graphTo = this.to;
    },
    mounted() {
        this.fetching = true;
    }
}
